import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, ViewChild } from '@angular/core'
import { thumbnailUrl } from '../utils/sale.util'

/**
 * A listing's photos, a swipe apart (scroll snap): "1/3", and arrows for a mouse. Fills its parent, which sets the
 * size (an aspect ratio). Only the first photo loads up front. Every photo gallery in the app: the house card (each
 * photo links to the house), the PPA card (a tap opens its details), and the house, sale, PPA and admin pages.
 */
@Component({
  selector: 'app-photo-strip',
  templateUrl: './photo-strip.component.html',
  styleUrls: ['./photo-strip.component.css'],
})
export class PhotoStripComponent implements OnChanges {
  /** The listing's photos (its `Media`), in order - the array itself, so it only changes when they do. */
  @Input() media: Array<{ url?: string | null }> | null | undefined = []

  /** Describes the photos ("Self contain in Ikeja, Lagos") - each one's alt adds its number. */
  @Input() alt = 'Photo'

  /** Where tapping a photo goes (a card's photos open its page); none, and they're just photos. */
  @Input() link: Array<string | number> | null = null

  /** A tap on a photo, when there's no `link` - a card that opens a dialog, say (set `tappable`, for the cursor). */
  @Output() photoTap = new EventEmitter<number>()
  @Input() tappable = false

  /** Shown with no photos, or for one that doesn't load: what the listing is. */
  @Input() placeholderIcon = 'image'

  /** The arrows: only while the pointer is over the card (`hover`), or always (a page's gallery). */
  @Input() arrows: 'hover' | 'always' = 'hover'

  /** How wide the photos are shown, for Cloudinary to size them (`sizes` says when). */
  @Input() width = 600
  @Input() sizes = '(min-width: 640px) 20rem, 100vw'

  @ViewChild('strip') strip?: ElementRef<HTMLElement>

  photos: Array<{ src: string; srcset: string | null }> = []
  index = 0
  failed = new Set<number>()

  // Here, not in a setter: `width` may arrive after `media`
  ngOnChanges(): void {
    this.photos = (this.media ?? [])
      .map((photo) => photo.url)
      .filter((url): url is string => !!url)
      .map((url) => {
        const small = thumbnailUrl(url, this.width)!
        return { src: small, srcset: small !== url ? `${small} ${this.width}w, ${thumbnailUrl(url, this.width * 2)} ${this.width * 2}w` : null }
      })
    this.index = 0
    this.failed.clear()
  }

  onScroll(strip: HTMLElement): void {
    this.index = Math.round(strip.scrollLeft / Math.max(strip.clientWidth, 1))
  }

  show(index: number): void {
    const strip = this.strip?.nativeElement
    if (!strip) return
    const target = Math.min(Math.max(index, 0), this.photos.length - 1)
    strip.scrollTo({ left: target * strip.clientWidth, behavior: 'smooth' })
  }
}
